"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  type BracketViewStrings,
  EliminationView,
  RoundRobinView,
} from "@/components/tools/random-fun/bracket-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  doubleElimination,
  drawOrder,
  MAX_ENTRANTS,
  MAX_ROUND_ROBIN,
  roundRobin,
  roundRobinMatches,
  singleElimination,
} from "@/lib/tool-kit/random-fun/brackets";

/** English strings. A translated content file overrides these through `ui`. */
export const TOURNAMENT_BRACKET_GENERATOR_STRINGS = {
  titleLabel: "Tournament name (printed above the bracket)",
  titlePlaceholder: "Spring Chess Championship",
  entrantsLabel: "Entrants — one per line",
  entrantsHint:
    "List them in seed order, strongest first, or choose a random draw. Up to {max} entrants ({robin} for a round robin).",
  entrantsExample: "Lions\nTigers\nBears\nWolves\nEagles\nHawks",
  example: "Fill in an example",
  clear: "Clear",
  formatLabel: "Format",
  formatSingle: "Single elimination",
  formatDouble: "Double elimination",
  formatRobin: "Round robin",
  seedingLabel: "Seeding",
  seedListed: "As listed (first is seed 1)",
  seedRandom: "Random draw",
  redraw: "Draw again",
  legsLabel: "Meetings",
  legsOne: "Each pair plays once",
  legsTwo: "Each pair plays twice (home and away)",
  print: "Print",
  results: "Your bracket",
  placeholder: "Add at least two entrants to see the bracket.",
  summarySingle:
    "{entrants} {entrantsWord}, a bracket of {size}, {matches} {matchesWord}.",
  summaryDouble:
    "{entrants} {entrantsWord}, a bracket of {size}, {matches} {matchesWord} before any reset final.",
  summaryRobin:
    "{entrants} {entrantsWord}, {rounds} {roundsWord}, {matches} {matchesWord}.",
  entrantsWord: "one: entrant|other: entrants",
  matchesWord: "one: match|other: matches",
  roundsWord: "one: round|other: rounds",
  byeNote: "With {byes} {byesWord}, seeds 1 to {byes} skip the first round.",
  byesWord: "one: bye|other: byes",
  resetNote:
    "If the entrant from the losers’ side wins the grand final, play it again: neither entrant has then lost twice, so a second match decides the title.",
  problemFew: "Add at least two entrants.",
  problemFewDouble: "Double elimination needs at least three entrants.",
  problemMany: "That is too many entrants. The limit is {max}.",
  winnerOf: "Winner of {match}",
  loserOf: "Loser of {match}",
  byeSlot: "Bye",
  roundFinal: "Final",
  roundSemi: "Semifinals",
  roundQuarter: "Quarterfinals",
  roundN: "Round {n}",
  winnersFinal: "Winners’ final",
  winnersSide: "Winners’ bracket",
  losersSide: "Losers’ bracket",
  losersFinal: "Losers’ final",
  losersRound: "Losers’ round {n}",
  grandFinal: "Grand final",
  matchLabel: "Match {id}",
  roundOfRobin: "Round {n}",
  rests: "{team} rests",
  versus: "{home} v {away}",
  standingsHeading: "Standings sheet",
  colTeam: "Team",
  colPlayed: "Played",
  colWon: "Won",
  colDrawn: "Drawn",
  colLost: "Lost",
  colPoints: "Points",
} as const;

type Strings = typeof TOURNAMENT_BRACKET_GENERATOR_STRINGS;
type Format = "single" | "double" | "robin";
type Seeding = "listed" | "random";

const LINE_BREAK = /\r?\n/;

function parseEntrants(text: string): string[] {
  return text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function BracketOutput({
  names,
  format,
  legs,
  strings,
}: {
  names: string[];
  format: Format;
  legs: "1" | "2";
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const plural = (key: string, n: number) => tPlural(key, locale, n);
  const viewStrings = strings as unknown as BracketViewStrings;
  const base = {
    entrants: fmt(names.length),
    entrantsWord: plural(strings.entrantsWord, names.length),
  };
  if (format === "robin") {
    const rounds = roundRobin(names.length, Number(legs) as 1 | 2);
    const matches = roundRobinMatches(names.length, Number(legs) as 1 | 2);
    return (
      <>
        <p className={HINT}>
          {t(strings.summaryRobin, {
            ...base,
            rounds: fmt(rounds.length),
            roundsWord: plural(strings.roundsWord, rounds.length),
            matches: fmt(matches),
            matchesWord: plural(strings.matchesWord, matches),
          })}
        </p>
        <RoundRobinView
          fmt={fmt}
          names={names}
          rounds={rounds}
          strings={viewStrings}
        />
      </>
    );
  }
  const double = format === "double";
  const bracket = double
    ? doubleElimination(names.length)
    : singleElimination(names.length);
  const summary = t(double ? strings.summaryDouble : strings.summarySingle, {
    ...base,
    size: fmt(bracket.size),
    matches: fmt(bracket.matches.length),
    matchesWord: plural(strings.matchesWord, bracket.matches.length),
  });
  const byeNote =
    bracket.byes > 0
      ? ` ${t(strings.byeNote, {
          byes: fmt(bracket.byes),
          byesWord: plural(strings.byesWord, bracket.byes),
        })}`
      : "";
  return (
    <>
      <p className={HINT}>{`${summary}${byeNote}`}</p>
      <EliminationView
        bracket={bracket}
        double={double}
        fmt={fmt}
        names={names}
        resetNote={strings.resetNote}
        strings={viewStrings}
      />
    </>
  );
}

export default function TournamentBracketGeneratorWidget() {
  const strings = useToolUi(TOURNAMENT_BRACKET_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [title, setTitle] = useState("");
  const [text, setText] = useState<string>(
    TOURNAMENT_BRACKET_GENERATOR_STRINGS.entrantsExample
  );
  const [format, setFormat] = useState<Format>("single");
  const [seeding, setSeeding] = useState<Seeding>("listed");
  const [legs, setLegs] = useState<"1" | "2">("1");
  const [order, setOrder] = useState<number[] | null>(null);

  const listed = parseEntrants(text);
  const names =
    seeding === "random" && order && order.length === listed.length
      ? order.map((i) => listed[i] as string)
      : listed;
  function drawSeeds(count: number) {
    setOrder(drawOrder(Array.from({ length: count }, (_, i) => i)));
  }

  function chooseSeeding(next: Seeding) {
    setSeeding(next);
    if (next === "random") {
      drawSeeds(listed.length);
    }
  }

  const limit = format === "robin" ? MAX_ROUND_ROBIN : MAX_ENTRANTS;
  const minimum = format === "double" ? 3 : 2;
  const tooMany = names.length > limit;
  const tooFew = names.length < minimum;
  const fewMessage =
    format === "double" && names.length === 2
      ? strings.problemFewDouble
      : strings.problemFew;

  return (
    <div className="space-y-6">
      <div className="space-y-6 print:hidden">
        <TextField
          id="tbg-title"
          label={strings.titleLabel}
          onChange={setTitle}
          placeholder={strings.titlePlaceholder}
          value={title}
        />
        <TextAreaField
          hint={t(strings.entrantsHint, {
            max: fmt(MAX_ENTRANTS),
            robin: fmt(MAX_ROUND_ROBIN),
          })}
          id="tbg-entrants"
          label={strings.entrantsLabel}
          onChange={setText}
          prose
          rows={8}
          value={text}
        />
        <div className="grid gap-6 lg:grid-cols-2">
          <PillGroup
            label={strings.formatLabel}
            name="tbg-format"
            onChange={setFormat}
            options={[
              { value: "single", label: strings.formatSingle },
              { value: "double", label: strings.formatDouble },
              { value: "robin", label: strings.formatRobin },
            ]}
            value={format}
          />
          <PillGroup
            label={strings.seedingLabel}
            name="tbg-seeding"
            onChange={chooseSeeding}
            options={[
              { value: "listed", label: strings.seedListed },
              { value: "random", label: strings.seedRandom },
            ]}
            value={seeding}
          />
        </div>
        {format === "robin" ? (
          <SelectField
            id="tbg-legs"
            label={strings.legsLabel}
            onChange={setLegs}
            options={[
              { value: "1", label: strings.legsOne },
              { value: "2", label: strings.legsTwo },
            ]}
            value={legs}
          />
        ) : null}
        <div className="flex flex-wrap gap-3">
          {seeding === "random" ? (
            <button
              className={BUTTON_PRIMARY}
              onClick={() => drawSeeds(listed.length)}
              type="button"
            >
              {strings.redraw}
            </button>
          ) : null}
          <button
            className={BUTTON_SECONDARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setText(strings.entrantsExample)}
            type="button"
          >
            {strings.example}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setText("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{title.trim() || strings.results}</h2>
        {names.length === 0 ? <Notice>{strings.placeholder}</Notice> : null}
        {names.length > 0 && (tooMany || tooFew) ? (
          <ErrorNotice>
            {tooMany ? t(strings.problemMany, { max: fmt(limit) }) : fewMessage}
          </ErrorNotice>
        ) : null}
        {names.length > 0 && !tooMany && !tooFew ? (
          <BracketOutput
            format={format}
            legs={legs}
            names={names}
            strings={strings}
          />
        ) : null}
      </section>
    </div>
  );
}
